import LayerRenderer from './layer-renderer';
//LAZY import {fabric} from 'fabric';
import * as log from 'loglevel';
import RenderUtil from './render-util';

import ColorizeUtil from './colorize-util';

class OverlayRenderer extends LayerRenderer {

    render(layer, context, canvas){

        // cached object with mask > reset to support conditional mask
        if(layer._object && layer._object.clipPath){
            delete layer._object.clipPath; // reset
        }

        // default params (copy to support changes without saving in editorMode config)
        let params = {
            // spread operator to make a shallow copy of the object with all the same properties
            ...layer.params
        };

        // NEW: if layer an option is referenced (Overlay values layer) store reference on option REQUIRED to support auto selection on productPreview._optionChanged
        if(layer.params.option && layer.params.interactive) { // interactive check could also be removed to always set the layer on the option but to be safe we currently leave the check
            let productOption = RenderUtil.getProductOption(layer.params.option, context.options);

            if (productOption) {
                // store layer reference
                productOption._layer = layer;
            }
        }

        // color reference from color option
        if(layer.params.optionColor){
            let productOption = RenderUtil.getProductOption(layer.params.optionColor, context.options);
            let resolved = RenderUtil.resolveOptionColor(productOption);

            if(resolved){
                // update color code
                if(resolved.color){
                    params.color = resolved.color;
                }

                // NEW: (SVG only) pattern img from option value  (only if no custom pattern provided)
                if(resolved.img){ // (UI no settings - only text has these setting): && params.textPattern && !params.urlPattern
                    params.urlPattern = resolved.img;
                }
            }
        }

        // UPDATE: the backend UI used to auto set autoScaleOverlay=true when crop=true , now we just set it when the crop mode is activated to scale the image once
        if(context.cropMode){ // in test this check was not necessary but we keep it to make it more explicit
            if(layer.params.crop && layer.type === 'upload'){
                // INFO: .params object is only temporary - the layer.params keeps the original value
                params.autoScaleOverlay = true;
            }
        }

        return this._getImage(layer, context, canvas, params).then(async (fabricImg) => {

            // add even if no image provided
            this._addRefRect(fabricImg, layer, context, canvas);

            if(fabricImg == null){
                return fabricImg;
            }

            // NEW: AI agents added unsupported params.width/height on an overlay layer - see the box guard after _applyAutoParams
            let fixDimension = layer.type === 'overlay' && (typeof params.width !== 'undefined' || typeof params.height !== 'undefined');
            // dimensions fabric derived from the loaded asset - has to be taken BEFORE _getOptions maps params.width/height onto the object. Once per object: _getImage caches layer._object and
            if(fixDimension && !fabricImg._contentSize){
                fabricImg._contentSize = { width: fabricImg.width, height: fabricImg.height };
            }

            // NEW: support for colorize for all layers types (only if color changed)
            if(params.color){
                let colorizeKey = ColorizeUtil.getKey(params.color, params.colorize);

                // or new object loaded (e.g. upload/product url changed) - layer._object is still the previous one here
                if(colorizeKey !== layer._colorizeKey || fabricImg !== layer._object){
                    await ColorizeUtil.colorize(fabricImg, params.color, layer, params);

                    layer._colorizeKey = colorizeKey;
                }
            }
            // NEW: (SVG only) support for color pattern (auto applied - no explicit flag like on text params.textPattern)
            if(params.urlPattern && fabricImg.type !== 'image'){
                await this._applyColorPattern(fabricImg, layer, params);
            }

            let options = this._getOptions(layer, context, params);

            fabricImg.set(options);

            // NEW: interactive image with controls > set controls to only allow scaling in ratio
            if(options.hasControls && !RenderUtil.isEditLayer(layer, context.editorMode, context.editLayer)){
                fabricImg.setControlsVisibility({mt: false, mb: false, ml: false, mr: false, bl: !options.lockScalingX, br: !options.lockScalingX, tl: !options.lockScalingY, tr: !options.lockScalingY, mtr: !options.lockRotation });
            }

            this._applyAutoParams(fabricImg, layer, context, params);

            if(fixDimension){
                /*
                    NEW: params.width/height is only supported on area layers (where it IS the geometry) and upload layers (the scale box) - an overlay layer never got the fields in the backend UI,
                    the object reports a box smaller than its own artwork and fabric sizes the render cache from object.width/height (_getCacheCanvasDimensions) - everything outside is silently CUT OFF.
                    INFO: objectCaching = false does NOT help - a layer with a refRect gets a clipPath and needsItsOwnCache() forces caching for those (an unclipped draw would erase the canvas below it, drawClipPathOnCache uses destination-in). So grow the box to cover the artwork instead.
                    Runs AFTER _applyAutoParams so the configured autoScale (refDimensions / params.width) and the position are already applied - re-anchoring on the center keeps the artwork exactly where it was

                    NOW prevented at the AGENT layer, but still old configs can be around. Could be removed with a config analysis across all configs.
                    Live config with the issue: clu9awuthlpq (layer=A-preview)
               */

                // once per layer - render runs again on every option change
                if(!layer._warnedSize){
                    layer._warnedSize = true;
                    log.warn('[live-options] unsupported params.width/height on overlay layer: ', layer.name, params.width + 'x' + params.height, ' asset: ', fabricImg._contentSize.width + 'x' + fabricImg._contentSize.height);
                }

                // only a box smaller than the asset clips - the others just carry a harmless stale value
                if(fabricImg._contentSize.width > fabricImg.width || fabricImg._contentSize.height > fabricImg.height){

                    let center = fabricImg.getCenterPoint();

                    fabricImg.set({
                        width: Math.max(fabricImg.width, fabricImg._contentSize.width),
                        height: Math.max(fabricImg.height, fabricImg._contentSize.height)
                    });

                    // the object renders its artwork centered on the box - keep that center to not move it
                    fabricImg.setPositionByOrigin(center, 'center', 'center');
                    fabricImg.setCoords();
                }
            }

            canvas.add(fabricImg);

            // cache reference on layer
            layer._object = fabricImg;

            return layer._object;
        });

    }

    _getOptions(layer, context, params = layer.params) {
        let options = super._getOptions(layer, context, params);

        // NEW: support for opacity
        if(params.opacity){
            options.opacity = params.opacity;
        }

        return options;
    }

    _getImage(layer, context, canvas, params = layer.params){
        // only load image once
        if(layer._object){

            /* test render delay:
            imagePromise = new Promise((resolve, reject) => {
                setTimeout(() => {
                    resolve(layer._object);
                }, 2000);
            }); */

            return Promise.resolve(layer._object);
        } else {
            return new Promise((resolve, reject) => {

                // NEW: always load SVG via loadSVGFromURL (required to support adding color after the object was already loaded)
                // INFO: some complex SVGs did not work with fabric.Image.fromURL
                if(params.url && params.url.indexOf('.svg') !== -1){

                    fabric.loadSVGFromURL(params.url, (objects, options) => {

                        if (objects === undefined) {

                            log.warn('layer url not found: ', params.url);

                            // still resolve and not reject to continue rendering
                            resolve(null);
                        }

                        let fabricSvg = fabric.util.groupSVGElements(objects, options);

                        resolve(fabricSvg);
                    }, null, { crossOrigin: 'anonymous'}); // crossOrigin required for canvas export support
                } else {

                    // NEW: image url with CDN size to dimension on preview (used to scale down large images)
                    let imgUrl = this._getImageUrl(layer.params.url, layer, context);

                    fabric.Image.fromURL(imgUrl, (fabricImg) => {

                        if (fabricImg === undefined) {

                            log.warn('layer url not found: ', params.url);

                            // still resolve and not reject to continue rendering
                            resolve(null);
                            return;
                        }

                        resolve(fabricImg);
                    }, { crossOrigin: 'anonymous'}); // crossOrigin required for canvas export support
                }

            });
        }
    }

    _applyColorPattern(object, layer, params) {

        if(object.type === 'image'){
            return Promise.resolve(object);
        }

        // pattern cache
        if(layer._pattern && layer._pattern._url === params.urlPattern){

            object.set('fill', layer._pattern);

            return Promise.resolve(layer._object);
        } else {
            return new Promise((resolve, reject) => {

                fabric.Image.fromURL(params.urlPattern, (fabricImg) => {

                    if (fabricImg === undefined) {

                        log.warn('color layer urlPattern not found: ', params.url);

                        // still resolve and not reject to continue rendering
                        resolve(null);
                        return;
                    }

                    // cache pattern obj (reset as .fill is overwritten with color)
                    layer._pattern = new fabric.Pattern({
                        source: fabricImg._element, // requires img tag
                        repeat: 'repeat',
                        // NEW: offset (only via manual params)
                        offsetY: params.patternOffsetY || 0,
                        offsetX: params.patternOffsetX || 0,
                    });

                    // store to compare for caching
                    layer._pattern._url = params.urlPattern;

                    object.set('fill', layer._pattern);

                    resolve(layer._object);

                }, { crossOrigin: 'anonymous'});
            });
        }

    }

}

export default OverlayRenderer;
